import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  Item,
  SearchSelect,
} from "src/components/Inputs/SearchSelect/SearchSelect";
import * as FSR from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import { SchoolFormId } from "../types";

interface RequestPaymentDialogProps {
  selectedFormSchools: SchoolFormId[];
  isOpen: boolean;
  onCancel: () => void;
  selectedTemplateId: string | null;
  onTemplateChange: (templateId: string | null) => void;
  onRefetch: () => void;
}

interface PaymentTemplateItem {
  id: string;
  name: string;
  description: string | null;
  amountInCents: number;
  currency: string;
}

export const RequestPaymentDialog: React.FC<RequestPaymentDialogProps> = ({
  selectedFormSchools,
  isOpen,
  onCancel,
  selectedTemplateId,
  onTemplateChange,
  onRefetch,
}) => {
  const toast = useAvelaToast();
  const organization = useOrganization();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState<Item[]>([]);
  const [templateDetails, setTemplateDetails] = useState<
    Map<string, PaymentTemplateItem>
  >(new Map());
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  const organizationId = organization.toNullable()?.id;

  useEffect(() => {
    if (isOpen && organizationId) {
      setIsLoadingTemplates(true);
      FSR.listPaymentTemplates({
        query: {
          organizationId,
          limit: 1000, // if there are more than 1000 payment templates then our search select breaks
          offset: 0,
        },
      })
        .then((response) => {
          if (!response.data) {
            throw new Error("No data returned from listPaymentTemplates");
          }
          const items: Item[] = response.data.data.paymentTemplates.map(
            (template) => ({
              key: template.id,
              label: template.name,
              description: template.description,
            })
          );
          setTemplates(items);

          const detailsMap = new Map<string, PaymentTemplateItem>();
          response.data.data.paymentTemplates.forEach((template) => {
            detailsMap.set(template.id, {
              id: template.id,
              name: template.name,
              description: template.description,
              amountInCents: template.amountInCents,
              currency: template.currency,
            });
          });
          setTemplateDetails(detailsMap);
        })
        .catch((error) => {
          console.error("Failed to load payment templates:", error);
          toast({
            title: "Failed to load payment templates",
            description: "Please try again.",
            status: "error",
          });
        })
        .finally(() => {
          setIsLoadingTemplates(false);
        });
    }
  }, [isOpen, organizationId, toast]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.key === selectedTemplateId) ?? null;
  }, [templates, selectedTemplateId]);

  const handleSubmit = async () => {
    if (!selectedTemplateId) return;

    const selectedTemplate = templateDetails.get(selectedTemplateId);
    if (!selectedTemplate) {
      toast({
        title: "Template not found",
        description: "Please select a valid payment template.",
        status: "error",
      });
      return;
    }

    const formIds = _.uniq(selectedFormSchools.map((sf) => sf.formId));

    setIsSubmitting(true);
    try {
      await FSR.createPayments({
        body: {
          payments: formIds.map((formId) => ({
            paymentTemplateId: selectedTemplateId,
            formId: formId,
            personId: null,
            amountInCents: selectedTemplate.amountInCents,
            currency: selectedTemplate.currency,
            paymentStatus: "pending",
            stripePaymentIntentId: null,
            paidBy: null,
          })),
        },
      });

      toast({
        title: "Payment requests created",
        description: "Families will be prompted for payment.",
        status: "success",
      });

      queryClient.invalidateQueries({ queryKey: ["payments"] });
      queryClient.invalidateQueries({ queryKey: ["paymentTemplates"] });
      onRefetch();
      onCancel();
    } catch (error) {
      console.error("Failed to create payment requests:", error);
      toast({
        title: "Failed to create payment requests",
        description: "Please try again.",
        status: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Request payment?</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Select a payment to request families they fulfill it, they'll be
              prompted for payment on their homepage.{" "}
              <Link
                color="primary.500"
                href="https://avelahelp.zendesk.com/hc/en-us/articles/47341300235021"
                textDecoration="underline"
              >
                Learn more
              </Link>
              .
            </Text>

            <SearchSelect
              label="Select payment to request"
              items={templates}
              selectedItem={selectedTemplate}
              onChange={(item) => onTemplateChange(item?.key ?? null)}
              placeholder="Select an option"
              disabled={isLoadingTemplates}
              variant="unlocked"
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={onCancel}
            isDisabled={isSubmitting}
          >
            No, cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            ml={3}
            isDisabled={!selectedTemplateId || isSubmitting}
            isLoading={isSubmitting}
          >
            Yes, request payment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
